import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Settings, Bell, BellOff, LayoutGrid, Loader2, ChevronRight, RotateCcw, MessageSquare, Shield, FileText, Mail, UserX, Lock, ThumbsUp, HelpCircle, Lightbulb, CalendarDays } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuPortal,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import API from '@/utils/api';
import { useAuth } from '@/contexts/AuthContext';
import { useDemo } from '@/contexts/DemoContext';
import DeleteAccountDialog from './DeleteAccountDialog';
import TwoFactorSettings from './Settings/TwoFactorSettings';

const SettingsDropdown = () => {
  const navigate = useNavigate();
  const { resetOnboarding } = useAuth();
  const { isDemoMode, getDemoSettings } = useDemo();
  const [settings, setSettings] = useState({
    notifications_enabled: true,
    default_home_view: 'tiles',
    calendar_auto_sync: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackType, setFeedbackType] = useState('general');
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  const [deleteAccountOpen, setDeleteAccountOpen] = useState(false);
  const [securitySettingsOpen, setSecuritySettingsOpen] = useState(false);

  useEffect(() => {
    if (isDemoMode) {
      // In demo mode, use demo settings and skip API call
      setSettings(getDemoSettings());
      setLoading(false);
    } else {
      fetchSettings();
    }
  }, [isDemoMode, getDemoSettings]);

  const fetchSettings = async () => {
    try {
      const response = await API.get('/settings');
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateSetting = async (key, value) => {
    setSaving(true);
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    
    // In demo mode, just update local state
    if (isDemoMode) {
      toast.success('Settings updated (demo)', { duration: 2000 });
      setSaving(false);
      return;
    }
    
    try {
      const response = await API.put('/settings', { [key]: value });
      setSettings(response.data);
      toast.success('Settings updated');
    } catch (error) {
      console.error('Failed to update settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleNotificationsToggle = (checked) => {
    setSettings(prev => ({ ...prev, notifications_enabled: checked }));
    updateSetting('notifications_enabled', checked);
  };

  const handleCalendarSyncToggle = (checked) => {
    setSettings(prev => ({ ...prev, calendar_auto_sync: checked }));
    updateSetting('calendar_auto_sync', checked);
    if (checked) {
      toast.success('Calendar sync enabled', {
        description: 'New dates will sync to your phone calendar automatically.'
      });
    }
  };

  const handleReplayOnboarding = async () => {
    await resetOnboarding();
    navigate('/');
  };

  const handleSubmitFeedback = async () => {
    if (!feedbackText.trim()) {
      toast.error('Please enter your feedback');
      return;
    }
    
    // In demo mode, just show success without API call
    if (isDemoMode) {
      toast.success('Thank you for your feedback! (demo mode)');
      setFeedbackOpen(false);
      setFeedbackText('');
      setFeedbackType('general');
      return;
    }
    
    setSubmittingFeedback(true);
    try {
      await API.post('/feedback', {
        type: feedbackType,
        message: feedbackText,
        timestamp: new Date().toISOString()
      });
      toast.success('Thank you for your feedback!');
      setFeedbackOpen(false);
      setFeedbackText('');
      setFeedbackType('general');
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      toast.error('Failed to send feedback. Please try again.');
    } finally {
      setSubmittingFeedback(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="p-2 sm:px-3 text-gray-500 hover:text-gray-700 hover:bg-gray-100"
          data-testid="settings-button"
          aria-label="Settings"
        >
          <Settings className="h-4 w-4" />
          <span className="hidden sm:inline ml-2">Settings</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        <DropdownMenuLabel className="flex items-center gap-2">
          <Settings className="h-4 w-4" />
          Settings
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        
        {loading ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
          </div>
        ) : (
          <>
            {/* Quick Notifications Toggle */}
            <div className="px-2 py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {settings.notifications_enabled ? (
                    <Bell className="h-4 w-4 text-green-600" />
                  ) : (
                    <BellOff className="h-4 w-4 text-gray-400" />
                  )}
                  <div>
                    <p className="text-sm font-medium">Notifications</p>
                    <p className="text-xs text-gray-500">
                      {settings.notifications_enabled ? 'Protection enabled' : 'All paused'}
                    </p>
                  </div>
                </div>
                <Switch
                  checked={settings.notifications_enabled}
                  onCheckedChange={handleNotificationsToggle}
                  disabled={saving}
                  data-testid="notifications-toggle"
                />
              </div>
            </div>
            
            {/* Link to Full Notification Settings */}
            <DropdownMenuItem 
              className="cursor-pointer"
              onClick={() => navigate('/settings/notifications')}
            >
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center gap-2">
                  <Bell className="h-4 w-4 text-blue-500" />
                  <span className="text-sm">Notification Settings</span>
                </div>
                <ChevronRight className="h-4 w-4 text-gray-400" />
              </div>
            </DropdownMenuItem>
            
            <DropdownMenuSeparator />
            
            {/* Calendar Auto-Sync Toggle */}
            <div className="px-2 py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CalendarDays className={`h-4 w-4 ${settings.calendar_auto_sync ? 'text-purple-600' : 'text-gray-400'}`} />
                  <div>
                    <p className="text-sm font-medium">Calendar Sync</p>
                    <p className="text-xs text-gray-500">
                      {settings.calendar_auto_sync ? 'Auto-sync enabled' : 'Manual add only'}
                    </p>
                  </div>
                </div>
                <Switch
                  checked={settings.calendar_auto_sync}
                  onCheckedChange={handleCalendarSyncToggle}
                  disabled={saving}
                  data-testid="calendar-sync-toggle"
                />
              </div>
            </div>
            
            {/* Link to Full Calendar Settings */}
            <DropdownMenuItem 
              className="cursor-pointer"
              onClick={() => navigate('/settings/calendar')}
              data-testid="calendar-settings-link"
            >
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center gap-2">
                  <CalendarDays className="h-4 w-4 text-purple-500" />
                  <span className="text-sm">Calendar Integration</span>
                </div>
                <ChevronRight className="h-4 w-4 text-gray-400" />
              </div>
            </DropdownMenuItem>
            
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              className="cursor-pointer"
              onClick={() => setSecuritySettingsOpen(true)}
              data-testid="security-settings-menu-item"
            >
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center gap-2">
                  <Lock className="h-4 w-4 text-green-500" />
                  <span className="text-sm">Security & 2FA</span>
                </div>
                <ChevronRight className="h-4 w-4 text-gray-400" />
              </div>
            </DropdownMenuItem>
            
            <DropdownMenuSeparator />
            
            {/* Replay Onboarding */}
            <DropdownMenuItem 
              className="cursor-pointer"
              onClick={handleReplayOnboarding}
              data-testid="replay-onboarding"
            >
              <div className="flex items-center gap-2">
                <RotateCcw className="h-4 w-4 text-slate-500" />
                <span className="text-sm">Replay welcome tour</span>
              </div>
            </DropdownMenuItem>
            
            {/* Security Audit Log */}
            <DropdownMenuItem 
              className="cursor-pointer"
              onClick={() => navigate('/admin/security')}
              data-testid="security-audit-menu-item"
            >
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center gap-2">
                  <Shield className="h-4 w-4 text-green-600" />
                  <span className="text-sm">Security Audit Log</span>
                </div>
                <ChevronRight className="h-4 w-4 text-gray-400" />
              </div>
            </DropdownMenuItem>
            
            <DropdownMenuSeparator />
            
            {/* Default Home View */}
            <div className="px-2 py-3">
              <div className="flex items-center gap-2 mb-2">
                <LayoutGrid className="h-4 w-4 text-blue-600" />
                <div>
                  <p className="text-sm font-medium">Default Home</p>
                  <p className="text-xs text-gray-500">6 Module Tiles</p>
                </div>
              </div>
            </div>
            
            <DropdownMenuSeparator />
            
            {/* Send Feedback */}
            <DropdownMenuItem 
              className="cursor-pointer"
              onClick={() => setFeedbackOpen(true)}
              data-testid="send-feedback"
            >
              <div className="flex items-center gap-2">
                <MessageSquare className="h-4 w-4 text-purple-500" />
                <span className="text-sm">Send Feedback</span>
              </div>
            </DropdownMenuItem>
            
            <DropdownMenuSeparator />
            
            {/* Legal Section */}
            <DropdownMenuSub>
              <DropdownMenuSubTrigger className="cursor-pointer">
                <div className="flex items-center gap-2">
                  <Shield className="h-4 w-4 text-slate-500" />
                  <span className="text-sm">Legal</span>
                </div>
              </DropdownMenuSubTrigger>
              <DropdownMenuPortal>
                <DropdownMenuSubContent>
                  <DropdownMenuItem 
                    className="cursor-pointer"
                    onClick={() => navigate('/privacy')}
                  >
                    <Shield className="h-4 w-4 mr-2 text-blue-500" />
                    <span>Privacy Policy</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    className="cursor-pointer"
                    onClick={() => navigate('/terms')}
                  >
                    <FileText className="h-4 w-4 mr-2 text-purple-500" />
                    <span>Terms of Service</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    className="cursor-pointer"
                    onClick={() => window.location.href = 'mailto:support@forgetmenot.app'}
                  >
                    <Mail className="h-4 w-4 mr-2 text-green-500" />
                    <span>Contact Support</span>
                  </DropdownMenuItem>
                </DropdownMenuSubContent>
              </DropdownMenuPortal>
            </DropdownMenuSub>
            
            <DropdownMenuSeparator />
            
            {/* Delete Account - at the bottom, subtle but accessible */}
            <DropdownMenuItem 
              className="cursor-pointer text-rose-600 focus:text-rose-700 focus:bg-rose-50"
              onClick={() => setDeleteAccountOpen(true)}
              data-testid="delete-account-menu-item"
            >
              <div className="flex items-center gap-2">
                <UserX className="h-4 w-4" />
                <span className="text-sm">Delete Account</span>
              </div>
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
      
      {/* Delete Account Dialog */}
      <DeleteAccountDialog 
        open={deleteAccountOpen} 
        onOpenChange={setDeleteAccountOpen} 
      />
      
      {/* Feedback Dialog */}
      <Dialog open={feedbackOpen} onOpenChange={setFeedbackOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-purple-500" />
              Share Your Feedback
            </DialogTitle>
            <DialogDescription>
              Help us improve! Your feedback shapes the future of Forget Me Not.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Feedback Type Buttons */}
            <div className="space-y-2">
              <p className="text-sm font-medium text-slate-700">What would you like to share?</p>
              <div className="flex gap-2">
                {[
                  { value: 'like', label: 'What I liked', icon: ThumbsUp },
                  { value: 'confused', label: 'Confused me', icon: HelpCircle },
                  { value: 'suggestion', label: 'Suggestion', icon: Lightbulb },
                ].map((type) => (
                  <Button
                    key={type.value}
                    variant={feedbackType === type.value ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setFeedbackType(type.value)}
                    className={feedbackType === type.value ? 'bg-purple-500 hover:bg-purple-600' : ''}
                  >
                    <type.icon className="h-4 w-4 mr-1.5" />
                    {type.label}
                  </Button>
                ))}
              </div>
            </div>
            
            {/* Feedback Text */}
            <div className="space-y-2">
              <Textarea
                placeholder={
                  feedbackType === 'like' 
                    ? "What's working well for you?" 
                    : feedbackType === 'confused'
                    ? "What was unclear or hard to find?"
                    : "What would make this app better for you?"
                }
                value={feedbackText}
                onChange={(e) => setFeedbackText(e.target.value)}
                className="min-h-[120px] resize-none"
                data-testid="feedback-textarea"
              />
              <p className="text-xs text-slate-500">
                Your feedback is anonymous and helps us build a better experience.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setFeedbackOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmitFeedback}
              disabled={submittingFeedback || !feedbackText.trim()}
              className="bg-purple-500 hover:bg-purple-600"
              data-testid="submit-feedback"
            >
              {submittingFeedback ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                'Send Feedback'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Security Settings Dialog */}
      <Dialog open={securitySettingsOpen} onOpenChange={setSecuritySettingsOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5 text-green-500" />
              Security Settings
            </DialogTitle>
            <DialogDescription>
              Manage your account security and two-factor authentication
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <TwoFactorSettings />
          </div>
        </DialogContent>
      </Dialog>
    </DropdownMenu>
  );
};

export default SettingsDropdown;
